import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './styles.css';

const serviceData = {
  development: {
    title: "Full-Stack Development",
    description: "Custom web applications built with modern technologies and best practices. From concept to deployment, we build scalable solutions that drive business growth.",
    features: [
      "Frontend development with React, Vue, or Angular",
      "Backend development with Node.js, Express, Python",
      "Database design with PostgreSQL, MongoDB, MySQL",
      "RESTful API development and integration",
      "Responsive design and mobile optimization",
      "Progressive Web Apps (PWA) development",
      "CI/CD pipeline setup and deployment"
    ],
    technologies: ["React", "Node.js", "PostgreSQL", "Express", "MongoDB", "Python", "Docker"],
    price: "Starting from $200",
    slug: "web-development",
    category: "Web Development"
  },
  setup: {
    title: "System Setup & Maintenance",
    description: "Complete hardware and software installation, configuration, and ongoing support to keep your systems running at peak performance.",
    features: [
      "Windows, macOS, and Linux installation",
      "Office software setup (Microsoft Office, Google Workspace)",
      "Driver updates and system optimization",
      "Regular maintenance and performance tuning",
      "Backup solutions and data migration",
      "Security software installation and configuration",
      "Remote monitoring and support"
    ],
    technologies: ["Windows", "macOS", "Linux", "Microsoft Office", "Antivirus", "Backup Solutions"],
    price: "Starting from $10 depending on systems & requirements",
    slug: "system-setup",
    category: "IT Support"
  },
  troubleshooting: {
    title: "Troubleshooting & Repair",
    description: "Quick diagnosis and resolution of hardware and software issues to minimize downtime and keep your business running smoothly.",
    features: [
      "Hardware diagnostics and repair",
      "Virus and malware removal",
      "Software conflict resolution",
      "Performance optimization",
      "Data recovery services",
      "Network connectivity issues",
      "Emergency support services"
    ],
    technologies: ["Diagnostic Tools", "Recovery Software", "Security Tools", "Hardware Testing"],
    price: "Starting from $10/hour",
    slug: "troubleshooting",
    category: "IT Support"
  },
  networking: {
    title: "Networking Solutions",
    description: "Complete network infrastructure setup and management for businesses and homes. Secure, reliable, and scalable network solutions.",
    features: [
      "LAN/WAN setup and configuration",
      "Wireless network installation",
      "Router and switch configuration",
      "Network security implementation",
      "VPN setup for remote access",
      "Network monitoring and management",
      "Structured cabling solutions"
    ],
    technologies: ["Cisco", "TP-Link", "Firewall", "VPN", "Wi-Fi 6", "Ubiquiti"],
    price: "Starting from $200 depending on project",
    slug: "networking",
    category: "Infrastructure"
  },
  consulting: {
    title: "IT Consulting",
    description: "Strategic technology planning and architecture design for your business needs. Get expert guidance on your technology journey.",
    features: [
      "Technology infrastructure planning",
      "System architecture design",
      "Security audit and recommendations",
      "Cloud migration strategy",
      "IT budget planning and optimization",
      "Digital transformation consulting",
      "Technology roadmap development"
    ],
    technologies: ["Cloud Solutions", "Security", "Architecture", "Strategy", "AWS", "Azure"],
    price: "Starting from $10/hour",
    slug: "consulting",
    category: "Consulting"
  }
};

export default function ServiceDetail() {
  const { serviceId } = useParams();
  const service = serviceData[serviceId];

  // Get the current URL for canonical
  const currentUrl = `https://raffcodes.tech/services/${serviceId}`;

  if (!service) {
    return (
      <>
        <Helmet>
          <title>Service Not Found | Raffcodes</title>
          <meta name="robots" content="noindex, follow" />
          <link rel="canonical" href="https://raffcodes.tech/services" />
        </Helmet>
        <div className="page service-detail">
          <div className="container">
            <div className="service-not-found">
              <h2>Service Not Found</h2>
              <p>The service you're looking for doesn't exist.</p>
              <Link to="/services" className="cta primary">View All Services</Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Structured Data for Service
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.title,
    "description": service.description,
    "provider": {
      "@type": "Organization",
      "name": "Raffcodes",
      "url": "https://raffcodes.tech"
    },
    "serviceType": service.category,
    "areaServed": {
      "@type": "City",
      "name": "Nairobi"
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": currentUrl,
      "availableLanguage": ["English", "Swahili"]
    },
    "offers": {
      "@type": "Offer",
      "price": service.price.replace('Starting from $', '').split('/')[0],
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{service.title} | Raffcodes - Professional IT Services in Nairobi</title>
        <meta name="description" content={`${service.title} - ${service.description} Available in Nairobi, Kenya. ${service.features.slice(0, 3).join('. ')}`} />
        <meta name="keywords" content={`${service.title.toLowerCase()}, ${service.category.toLowerCase()}, IT services Nairobi, ${service.technologies.join(', ')}, Raffcodes`} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={currentUrl} />

        {/* Open Graph Tags */}
        <meta property="og:title" content={`${service.title} | Raffcodes - IT Services`} />
        <meta property="og:description" content={`${service.title} - ${service.description}`} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`https://raffcodes.tech/images/services/${service.slug}.jpg`} />
        <meta property="og:site_name" content="Raffcodes" />
        <meta property="og:locale" content="en_KE" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${service.title} | Raffcodes`} />
        <meta name="twitter:description" content={service.description} />
        <meta name="twitter:image" content={`https://raffcodes.tech/images/services/${service.slug}.jpg`} />
        <meta name="twitter:site" content="@raffcodes" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="page service-detail">
        {/* Hero Section - Enhanced */}
        <section className="service-hero">
          <div className="container">
            <div className="service-hero-content">
              <div className="service-breadcrumb">
                <Link to="/services">Services</Link>
                <span className="breadcrumb-separator">/</span>
                <span className="breadcrumb-current">{service.title}</span>
              </div>
              <span className="service-category">{service.category}</span>
              <h1>{service.title}</h1>
              <p className="service-description">{service.description}</p>
              <div className="service-price-wrapper">
                <span className="service-price-label">Pricing</span>
                <span className="service-price">{service.price}</span>
              </div>
              <div className="service-hero-actions">
                <a href="/contact" className="cta primary">
                  <i className="fas fa-rocket"></i> Get This Service
                </a>
                <a href="/contact" className="cta secondary">
                  <i className="fas fa-comment"></i> Ask a Question
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section - Enhanced */}
        <section className="service-features">
          <div className="container">
            <div className="section-header">
              <span className="section-tag">What's Included</span>
              <h2>Service Features</h2>
            </div>
            <div className="features-grid">
              {service.features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <div className="feature-icon-wrapper">
                    <i className="fas fa-check-circle"></i>
                  </div>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technologies Section - Enhanced */}
        <section className="service-technologies">
          <div className="container">
            <div className="section-header">
              <span className="section-tag">Tools & Stack</span>
              <h2>Technologies We Use</h2>
            </div>
            <div className="tech-tags">
              {service.technologies.map((tech, index) => (
                <span key={index} className="tech-tag">
                  <i className="fas fa-code"></i> {tech}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="service-stats">
          <div className="container">
            <div className="stats-grid">
              <div className="stat-card">
                <i className="fas fa-clock"></i>
                <div>
                  <span className="stat-number">24/7</span>
                  <span className="stat-label">Support Available</span>
                </div>
              </div>
              <div className="stat-card">
                <i className="fas fa-users"></i>
                <div>
                  <span className="stat-number">50+</span>
                  <span className="stat-label">Happy Clients</span>
                </div>
              </div>
              <div className="stat-card">
                <i className="fas fa-check-circle"></i>
                <div>
                  <span className="stat-number">98%</span>
                  <span className="stat-label">Success Rate</span>
                </div>
              </div>
              <div className="stat-card">
                <i className="fas fa-phone"></i>
                <div>
                  <span className="stat-number">Quick</span>
                  <span className="stat-label">Response Time</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="service-cta">
          <div className="container">
            <div className="cta-content">
              <div className="cta-text">
                <h2>Ready to Get Started?</h2>
                <p>Contact us today to discuss your project and get a free consultation.</p>
              </div>
              <div className="cta-buttons">
                <a href="/contact" className="cta primary">
                  <i className="fas fa-paper-plane"></i> Contact Us
                </a>
                <Link to="/services" className="cta secondary">
                  <i className="fas fa-th-list"></i> View All Services
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}